<template>
  <div class="page">
    <AppHeader />

    <main class="product-page">
      <router-link to="/shop" class="back-link">← Back to shop</router-link>

      <p v-if="loading" class="product-status">Loading…</p>
      <p v-else-if="error" class="product-status product-status--error">
        Couldn't load this product: {{ error }}
      </p>

      <div v-else-if="product" class="product-layout">
        <div class="product-gallery">
          <ProductGallery v-if="product.images.length" :images="product.images" :title="product.title" />
          <PlaceholderArt v-else class="product-gallery__placeholder" :label="product.title" />
        </div>

        <div class="product-details">
          <span v-if="!product.availableForSale" class="badge badge--sold">Sold Out</span>
          <h1 class="product-details__title">{{ product.title }}</h1>
          <p class="product-details__price">{{ formatPrice(product) }}</p>

          <p v-if="product.description" class="product-details__description">
            {{ product.description }}
          </p>

          <button
            class="btn btn--dark product-details__buy"
            :disabled="!product.availableForSale || cart.loading"
            @click="buyNow"
          >
            {{ product.availableForSale ? 'Add to Cart' : 'Sold Out' }}
          </button>

          <p v-if="cart.error" class="product-details__error">{{ cart.error }}</p>

          <ul class="product-details__meta">
            <li>An original piece, every item is one of a kind</li>
          </ul>
        </div>
      </div>

      <p v-else class="product-status">
        We couldn't find that product. <router-link to="/">Back to shop</router-link>
      </p>
    </main>

    <SiteFooter />
  </div>
</template>

<script>
import { useCart } from '../shopify/cart'
import { formatMoney } from '../shopify/money'
import { fetchProductByHandle } from '../shopify/products'
import AppHeader from '../components/AppHeader.vue'
import PlaceholderArt from '../components/PlaceholderArt.vue'
import ProductGallery from '../components/ProductGallery.vue'
import SiteFooter from '../components/home/SiteFooter.vue'

export default {
  name: 'ProductPage',

  components: { AppHeader, PlaceholderArt, ProductGallery, SiteFooter },

  props: {
    handle: { type: String, required: true },
  },

  setup() {
    const { state: cart, addToCart, openDrawer } = useCart()
    return { cart, addToCart, openDrawer }
  },

  data() {
    return {
      product: null,
      loading: true,
      error: null,
    }
  },

  watch: {
    handle: {
      immediate: true,
      handler() {
        this.loadProduct()
      },
    },
  },

  methods: {
    async loadProduct() {
      this.loading = true
      this.error = null
      this.product = null
      try {
        this.product = await fetchProductByHandle(this.handle)
      } catch (err) {
        this.error = err.message
      } finally {
        this.loading = false
      }
    },
    formatPrice(product) {
      return formatMoney(product.price, product.currency)
    },
    async buyNow() {
      if (!this.product?.variantId || !this.product.availableForSale) return
      try {
        await this.addToCart(this.product.variantId)
        this.openDrawer()
      } catch {
        // error surfaced via cart.error above the button
      }
    },
  },
}
</script>

<style scoped>
.page {
  min-height: 100vh;
  background: var(--canvas);
  color: var(--ink);
}

.product-page {
  max-width: var(--max-w);
  margin: 0 auto;
  padding: 4rem;
}

.back-link {
  display: inline-block;
  font-size: 1.3rem;
  color: var(--stone);
  margin-bottom: 3.6rem;
}
.back-link:hover { color: var(--ink); }

.product-status {
  font-size: 1.4rem;
  color: var(--stone);
  padding: 4rem 0;
}
.product-status--error { color: #b3261e; }
.product-status a { color: var(--ink); text-decoration: underline; }

.product-layout {
  display: grid;
  grid-template-columns: minmax(0, 1.2fr) minmax(0, 1fr);
  gap: 6.4rem;
  align-items: start;
}

.product-gallery {
  position: sticky;
  top: 10rem;
  min-width: 0;
}

.product-gallery__placeholder {
  aspect-ratio: 4 / 5;
}

.product-details {
  padding-top: 0.8rem;
}

.badge {
  display: inline-block;
  background: var(--ink);
  color: var(--canvas);
  font-size: 1rem;
  font-weight: 500;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  padding: 0.5rem 1rem;
  margin-bottom: 1.6rem;
}

.product-details__title {
  font-size: clamp(3.6rem, 4vw, 4.8rem);
  font-weight: 400;
  line-height: 1.1;
  margin-bottom: 0.8rem;
}

.product-details__price {
  font-size: 2rem;
  color: var(--charcoal);
  margin-bottom: 2.4rem;
}

.product-details__description {
  font-size: 1.5rem;
  line-height: 1.7;
  color: var(--charcoal);
  margin-bottom: 3.2rem;
  white-space: pre-line;
}

.product-details__buy {
  width: 100%;
  margin-bottom: 1.6rem;
}
.product-details__buy:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.product-details__error {
  color: #b3261e;
  font-size: 1.3rem;
  margin-bottom: 1.6rem;
}

.product-details__meta {
  list-style: none;
  border-top: 1px solid var(--linen);
  padding-top: 2rem;
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
  font-size: 1.3rem;
  color: var(--stone);
}

@media (max-width: 960px) {
  .product-page { padding: 2.4rem 1.6rem; }
  .product-layout { grid-template-columns: 1fr; gap: 3.2rem; }
  .product-gallery { position: static; margin: 0 -1.6rem; }
  .product-gallery :deep(.gallery__thumbs) { padding: 0 1.6rem; }
}
</style>
